import { useCallback } from 'react';
import type { MenuProps } from 'antd';
import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import type { CalendarRow } from '../../../../types/calendar';
import { usePlanDetailsContext } from '../../../context/PlanDetailsContext';
import { useResetRuleSession } from '../../../../rules/store/useResetRuleSession';

interface UseCalendarRowActionsOptions {
    onRequestDeleteEvent?: (record: CalendarRow) => void;
}

export function useCalendarRowActions(options?: UseCalendarRowActionsOptions) {
    const { defaultRowActionItems } = useTpoGenConfig();
    const { setActiveView, setEditEventId, setEditPromoId, setAutoAddPromo } =
        usePlanDetailsContext();
    const { resetEventSession } = useResetRuleSession();

    const { onRequestDeleteEvent } = options ?? {};

    const handleEditEvent = useCallback(
        (record: CalendarRow) => {
            resetEventSession();
            setActiveView('event');
            setEditEventId(record.event_id);
        },
        [resetEventSession, setActiveView, setEditEventId],
    );

    const handleEditPromo = useCallback(
        (record: CalendarRow) => {
            resetEventSession();
            setActiveView('event');
            setEditEventId(record.event_id);
            setEditPromoId(record.promo_id);
        },
        [resetEventSession, setActiveView, setEditEventId, setEditPromoId],
    );

    const handleAddPromo = useCallback(
        (record: CalendarRow) => {
            resetEventSession();
            setActiveView('event');
            setEditEventId(record.event_id);
            setAutoAddPromo(true);
        },
        [resetEventSession, setActiveView, setEditEventId, setAutoAddPromo],
    );

    const getRowActionMenuItems = useCallback(
        (record: CalendarRow): MenuProps['items'] => {
            return defaultRowActionItems.map((item) => {
                if ('type' in item) return item;
                return {
                    key: item.key,
                    label: item.danger ? <span className="text-[#FF4D4F]">{item.label}</span> : item.label,
                    disabled: item.disabled,
                    onClick: () => {
                        if (item.key === 'edit_event') {
                            handleEditEvent(record);
                        }
                        if (item.key === 'add_promo') {
                            handleAddPromo(record);
                        }
                        if (item.key === 'edit_promo') {
                            handleEditPromo(record);
                        }
                        if (item.key === 'delete_event') {
                            onRequestDeleteEvent?.(record);
                        }
                    },
                };
            });
        },
        [defaultRowActionItems, handleEditEvent, handleAddPromo, handleEditPromo, onRequestDeleteEvent],
    );

    return { getRowActionMenuItems, handleEditEvent, handleEditPromo };
}
